import React, { useState, useEffect } from "react";
import { Plus, Package, AlertCircle, Pencil } from "lucide-react";
import PageLayout from "../../components/PageLayout";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import {
  deliveries as api,
  units as unitsApi,
  residents as residentsApi,
  type Delivery,
  type Unit,
  type Resident,
  type ApiError,
} from "../../services/api";
import { useServerTable } from "../../hooks/useServerTable";
import { useStatusCounts } from "../../hooks/useStatusCounts";
import DataTable from "../../components/DataTable";
import StatusFilterBar from "../../components/StatusFilterBar";
import SearchSelect from "../../components/SearchSelect";

const inp = {
  width: "100%",
  padding: "9px 12px",
  background: "var(--nx-surface-3)",
  border: "1px solid var(--nx-border)",
  borderRadius: 8,
  fontSize: 13,
  color: "var(--nx-text-primary)",
  outline: "none",
  boxSizing: "border-box" as const,
};
const lbl = {
  display: "block",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--nx-text-secondary)",
  marginBottom: 5,
  letterSpacing: "0.05em",
  textTransform: "uppercase" as const,
};

const INIT = {
  carrier: "",
  trackingCode: "",
  description: "",
  unitId: "",
  residentId: "",
};

// Natural sort (block, then unit) so "A2" sorts before "A10" rather than after.
const sortUnits = (list: Unit[]) =>
  [...list].sort((a, b) =>
    (a.blockName || "").localeCompare(b.blockName || "", undefined, { numeric: true, sensitivity: "base" })
    || (a.unitNumber || "").localeCompare(b.unitNumber || "", undefined, { numeric: true, sensitivity: "base" }));

const statusColor = (s: string) =>
  s === "arrived"
    ? { bg: "rgba(245,158,11,0.1)", text: "#D97706" }
    : s === "notified"
      ? { bg: "rgba(26,108,246,0.1)", text: "#1A6CF6" }
      : s === "collected"
        ? { bg: "rgba(16,185,129,0.1)", text: "#10B981" }
        : { bg: "rgba(148,163,184,0.1)", text: "#64748B" };

const statusOptions: Delivery["status"][] = [
  "arrived",
  "notified",
  "collected",
  "returned",
];

const DELIVERY_STATUS_FILTERS = [
  "all",
  "arrived",
  "notified",
  "collected",
  "returned",
] as const;
type DeliveryStatusFilter = (typeof DELIVERY_STATUS_FILTERS)[number];

const DeliveriesPage: React.FC = () => {
  const { user } = useAuth();
  const societyId = user?.societyId ?? null;
  // Browsing the full unit directory (GET /units list) is admin/staff-only on the
  // backend (see SecurityConfig) — residents/tenants logging a delivery only ever
  // need their own unit, which we derive from their own resident record instead.
  const canBrowseUnits = user?.role !== "RESIDENT" && user?.role !== "TENANT";
  const [unitOptions, setUnitOptions] = useState<Unit[]>([]);
  const [residentOptions, setResidentOptions] = useState<Resident[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [form, setForm] = useState(INIT);
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState(INIT);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<DeliveryStatusFilter>("all");

  useEffect(() => {
    if (!societyId) return;
    if (canBrowseUnits) {
      unitsApi
        .list(societyId, { size: 500 })
        .then((page) => setUnitOptions(sortUnits(page.content)))
        .catch(() => {});
    }
    residentsApi
      .list(societyId, { size: 500 })
      .then((page) => setResidentOptions(page.content))
      .catch(() => {});
  }, [societyId, canBrowseUnits]);

  // Residents/tenants can't browse the unit directory, but they only ever log a
  // delivery for their own unit anyway — synthesize that one option from their
  // own (already-fetched, self-scoped) resident record.
  useEffect(() => {
    if (canBrowseUnits) return;
    const own = residentOptions[0];
    if (!own) return;
    setUnitOptions([{
      id: own.unitId,
      societyId: own.societyId,
      unitNumber: own.unitNumber ?? "",
      blockName: own.blockName,
      status: "occupied",
      createdAt: "",
    }]);
    setForm((p) => (p.unitId ? p : { ...p, unitId: own.unitId }));
  }, [canBrowseUnits, residentOptions]);

  const {
    rows,
    totalElements,
    totalPages,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    sortField,
    sortDir,
    setSort,
    refetch,
  } = useServerTable<Delivery>(
    (params) =>
      societyId
        ? api.listPage(societyId, {
            ...params,
            status: statusFilter === "all" ? undefined : statusFilter,
          })
        : Promise.resolve({ content: [], totalElements: 0, totalPages: 0 }),
    [societyId, statusFilter],
  );

  const {
    counts: statusCounts,
    loading: statusCountsLoading,
    refetch: refetchStatusCounts,
  } = useStatusCounts(
    DELIVERY_STATUS_FILTERS,
    (key) =>
      societyId
        ? api
            .listPage(societyId, {
              page: 0,
              size: 1,
              status: key === "all" ? undefined : key,
            })
            .then((r) => r.totalElements)
        : Promise.resolve(0),
    [societyId],
  );

  const change = (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId) return;
    setSaving(true);
    setFormError("");
    try {
      await api.create(societyId, {
        carrier: form.carrier || undefined,
        trackingCode: form.trackingCode || undefined,
        description: form.description || undefined,
        unitId: form.unitId || undefined,
        residentId: form.residentId || undefined,
      });
      refetch();
      refetchStatusCounts();
      setShowForm(false);
      setForm(INIT);
    } catch (err) {
      setFormError((err as ApiError).message || "Failed to log delivery");
    } finally {
      setSaving(false);
    }
  };

  const unitLabel = (id?: string) => {
    const u = unitOptions.find((u) => u.id === id);
    if (!u) return "—";
    return u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber;
  };
  const residentLabel = (id?: string) => residentOptions.find((r) => r.id === id)?.fullName || "—";

  const residentsForUnit = (unitId: string) =>
    unitId ? residentOptions.filter((r) => r.unitId === unitId) : residentOptions;

  const editChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setEditForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const openEdit = (d: Delivery) => {
    setEditId(d.id);
    setEditForm({
      carrier: d.carrier || "",
      trackingCode: d.trackingCode || "",
      description: d.description || "",
      unitId: d.unitId || "",
      residentId: d.residentId || "",
    });
    setEditError("");
  };
  const closeEdit = () => { setEditId(null); setEditForm(INIT); setEditError(""); };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!societyId || !editId) return;
    setEditSaving(true);
    setEditError("");
    try {
      await api.update(societyId, editId, {
        carrier: editForm.carrier || undefined,
        trackingCode: editForm.trackingCode || undefined,
        description: editForm.description || undefined,
        unitId: editForm.unitId || undefined,
        residentId: editForm.residentId || undefined,
      });
      refetch();
      closeEdit();
    } catch (err) {
      setEditError((err as ApiError).message || "Failed to update delivery");
    } finally {
      setEditSaving(false);
    }
  };

  const updateStatus = async (id: string, status: Delivery["status"]) => {
    if (!societyId) return;
    setUpdatingId(id);
    setStatusError(null);
    try {
      await api.updateStatus(societyId, id, status);
      refetch();
      refetchStatusCounts();
    } catch (err) {
      setStatusError(
        (err as ApiError).message || "Failed to update delivery status.",
      );
    } finally {
      setUpdatingId(null);
    }
  };

  const fmt = (iso: string) =>
    new Date(iso).toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <PageLayout>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: "Syne, sans-serif",
              fontSize: 22,
              fontWeight: 700,
              color: "var(--nx-text-primary)",
              letterSpacing: "-0.03em",
              marginBottom: 4,
            }}
          >
            Deliveries
          </h1>
          <p style={{ fontSize: 13, color: "var(--nx-text-muted)" }}>
            {totalElements} {statusFilter !== "all" ? statusFilter : "total"}{" "}
            deliveries
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="nx-btn nx-btn-primary"
          style={{ gap: 6 }}
        >
          <Plus size={14} /> Log Delivery
        </button>
      </div>

      {statusError && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "10px 14px",
            background: "rgba(239,68,68,0.08)",
            border: "1px solid rgba(239,68,68,0.2)",
            borderRadius: 8,
            marginBottom: 16,
            fontSize: 13,
            color: "#EF4444",
          }}
        >
          <AlertCircle size={14} style={{ flexShrink: 0 }} />
          {statusError}
          <button
            onClick={() => setStatusError(null)}
            style={{
              marginLeft: "auto",
              background: "none",
              border: "none",
              color: "#EF4444",
              cursor: "pointer",
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>
      )}

      <StatusFilterBar
        options={DELIVERY_STATUS_FILTERS.map((f) => ({
          key: f,
          label: f === "all" ? "All" : f.charAt(0).toUpperCase() + f.slice(1),
        }))}
        active={statusFilter}
        onChange={setStatusFilter}
        counts={statusCounts}
        countsLoading={statusCountsLoading}
      />

      <div className="nx-card" style={{ padding: 0, overflow: "hidden" }}>
        <DataTable
          columns={[
            {
              key: "carrier",
              header: "Carrier",
              cellStyle: { fontWeight: 500 },
              sortable: true,
              sortKey: "carrier",
              render: (d: Delivery) => d.carrier || "—",
            },
            {
              key: "tracking",
              header: "Tracking Code",
              cellStyle: {
                color: "var(--nx-text-muted)",
                fontFamily: "monospace",
                fontSize: 11,
              },
              sortable: true,
              sortKey: "trackingCode",
              render: (d: Delivery) => d.trackingCode || "—",
            },
            {
              key: "description",
              header: "Description",
              cellStyle: {
                color: "var(--nx-text-secondary)",
                maxWidth: 160,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              },
              sortable: true,
              sortKey: "description",
              render: (d: Delivery) => d.description || "—",
            },
            {
              key: "unit",
              header: "Unit",
              cellStyle: { color: "var(--nx-text-muted)" },
              render: (d: Delivery) => unitLabel(d.unitId),
            },
            {
              key: "resident",
              header: "Resident",
              cellStyle: { color: "var(--nx-text-secondary)" },
              render: (d: Delivery) => residentLabel(d.residentId),
            },
            {
              key: "status",
              header: "Status",
              sortable: true,
              sortKey: "status",
              render: (d: Delivery) => {
                const sc = statusColor(d.status);
                return (
                  <span
                    style={{
                      padding: "2px 8px",
                      borderRadius: 99,
                      fontSize: 10,
                      fontWeight: 500,
                      background: sc.bg,
                      color: sc.text,
                    }}
                  >
                    {d.status.charAt(0).toUpperCase() + d.status.slice(1)}
                  </span>
                );
              },
            },
            {
              key: "arrived",
              header: "Arrived At",
              cellStyle: { color: "var(--nx-text-muted)" },
              sortable: true,
              sortKey: "arrivedAt",
              render: (d: Delivery) => fmt(d.arrivedAt),
            },
            {
              key: "update",
              header: "Update Status",
              render: (d: Delivery) => (
                <select
                  value={d.status}
                  disabled={updatingId === d.id}
                  onChange={(ev) =>
                    updateStatus(d.id, ev.target.value as Delivery["status"])
                  }
                  style={{
                    ...inp,
                    padding: "4px 8px",
                    fontSize: 11,
                    width: "auto",
                  }}
                >
                  {statusOptions.map((s) => (
                    <option key={s} value={s}>
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: "actions",
              header: "",
              cellStyle: { textAlign: "right", whiteSpace: "nowrap" },
              render: (d: Delivery) => (
                <button
                  onClick={() => openEdit(d)}
                  style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", borderRadius: 6, color: "var(--nx-text-muted)" }}
                  title="Edit delivery"
                >
                  <Pencil size={14} />
                </button>
              ),
            },
          ]}
          rows={rows}
          rowKey={(d) => d.id}
          loading={loading}
          emptyIcon={
            <Package size={32} style={{ color: "var(--nx-text-muted)" }} />
          }
          emptyText="No deliveries logged yet."
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
          sortField={sortField}
          sortDir={sortDir}
          onSortChange={setSort}
          pageSize={pageSize}
          onPageSizeChange={setPageSize}
        />
      </div>

      {showForm && (
        <Modal
          title="Log Delivery"
          submitLabel="Log Delivery"
          error={formError}
          isLoading={saving}
          onClose={() => {
            setShowForm(false);
            setForm(INIT);
            setFormError("");
          }}
          onSubmit={handleSubmit}
        >
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Carrier</label>
              <input
                name="carrier"
                value={form.carrier}
                onChange={change}
                placeholder="Amazon, Delhivery…"
                style={inp}
              />
            </div>
            <div>
              <label style={lbl}>Tracking Code</label>
              <input
                name="trackingCode"
                value={form.trackingCode}
                onChange={change}
                placeholder="Track#123"
                style={inp}
              />
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <input
              name="description"
              value={form.description}
              onChange={change}
              placeholder="Package contents or notes"
              style={inp}
            />
          </div>
          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}
          >
            <div>
              <label style={lbl}>Unit ID</label>
              <SearchSelect
                value={form.unitId}
                onChange={(v) => setForm((p) => ({ ...p, unitId: v, residentId: "" }))}
                options={unitOptions.map((u) => ({
                  value: u.id,
                  label: u.blockName
                    ? `${u.blockName} — ${u.unitNumber}`
                    : u.unitNumber,
                }))}
                placeholder={
                  unitOptions.length === 0
                    ? "No units available"
                    : "Select unit…"
                }
                emptyText="No units found"
                disabled={unitOptions.length === 0}
              />
            </div>
            <div>
              <label style={lbl}>Resident ID</label>
              <SearchSelect
                value={form.residentId}
                onChange={(v) => setForm((p) => ({ ...p, residentId: v }))}
                options={residentsForUnit(form.unitId).map((r) => ({
                  value: r.id,
                  label: `${r.fullName}${r.unitNumber ? ` — ${r.unitNumber}` : ""}`,
                }))}
                placeholder={
                  residentsForUnit(form.unitId).length === 0
                    ? "No residents available"
                    : "Select resident…"
                }
                emptyText="No residents found"
                disabled={residentsForUnit(form.unitId).length === 0}
              />
            </div>
          </div>
        </Modal>
      )}

      {editId && (
        <Modal
          title="Edit Delivery"
          submitLabel="Save Changes"
          error={editError}
          isLoading={editSaving}
          onClose={closeEdit}
          onSubmit={handleEditSubmit}
        >
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Carrier</label>
              <input name="carrier" value={editForm.carrier} onChange={editChange} placeholder="Amazon, Delhivery…" style={inp} />
            </div>
            <div>
              <label style={lbl}>Tracking Code</label>
              <input name="trackingCode" value={editForm.trackingCode} onChange={editChange} placeholder="Track#123" style={inp} />
            </div>
          </div>
          <div>
            <label style={lbl}>Description</label>
            <input name="description" value={editForm.description} onChange={editChange} placeholder="Package contents or notes" style={inp} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <label style={lbl}>Unit</label>
              <SearchSelect
                value={editForm.unitId}
                onChange={(v) => setEditForm((p) => ({ ...p, unitId: v, residentId: "" }))}
                options={unitOptions.map((u) => ({
                  value: u.id,
                  label: u.blockName ? `${u.blockName} — ${u.unitNumber}` : u.unitNumber,
                }))}
                placeholder={unitOptions.length === 0 ? "No units available" : "Select unit…"}
                emptyText="No units found"
                disabled={unitOptions.length === 0}
              />
            </div>
            <div>
              <label style={lbl}>Resident</label>
              <SearchSelect
                value={editForm.residentId}
                onChange={(v) => setEditForm((p) => ({ ...p, residentId: v }))}
                options={residentsForUnit(editForm.unitId).map((r) => ({
                  value: r.id,
                  label: `${r.fullName}${r.unitNumber ? ` — ${r.unitNumber}` : ""}`,
                }))}
                placeholder={residentsForUnit(editForm.unitId).length === 0 ? "No residents available" : "Select resident…"}
                emptyText="No residents found"
                disabled={residentsForUnit(editForm.unitId).length === 0}
              />
            </div>
          </div>
        </Modal>
      )}
    </PageLayout>
  );
};

export default DeliveriesPage;
